import React, { useCallback, useEffect, useState } from "react";
import { Modal, Icon } from "./common";
import { Plus, Pencil, Ban, CheckCircle2, Trash2, RotateCcw } from "lucide-react";
import { useLang } from "../i18n";
import { useConfig } from "../context/ConfigContext";
import api, { apiError } from "../api";
import { toast } from "sonner";

const FIELDS = [
  { key: "points", labelKey: "teacher.ws_points_label", defAr: "النقاط", defEn: "Points" },
  { key: "badges", labelKey: "teacher.ws_badge_label", defAr: "الشارات", defEn: "Badges" },
  { key: "challenges", labelKey: "teacher.ws_challenge_label", defAr: "التحديات", defEn: "Challenges" },
  { key: "leaderboard", labelKey: "teacher.ws_leaderboard_label", defAr: "الترتيب", defEn: "Ranking" },
  { key: "students", labelKey: "teacher.ws_student_label", defAr: "الطالبات", defEn: "Students" },
];

const CAT_ICONS = ["Star", "MessageCircleMore", "NotebookPen", "HeartHandshake", "Trophy", "Target", "Clock", "AlertTriangle", "BookOpen", "Sparkles"];

const dispName = (pick, o) => {
  const primary = pick(o, "name") || o.name_ar;
  const secondary = primary === o.name_ar ? o.name_en : o.name_ar;
  return secondary && secondary !== primary ? `${primary} — ${secondary}` : primary;
};

function CategoriesSection() {
  const { t, pick } = useLang();
  const { reload } = useConfig();
  const [own, setOwn] = useState(null);
  const [globalCats, setGlobalCats] = useState([]);
  const [form, setForm] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/staff/workspace-categories");
    setOwn(data.own);
    const ownKeys = new Set(data.own.map((x) => x.key));
    setGlobalCats(data.global.filter((g) => !ownKeys.has(g.key)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => setForm({ id: null, globalKey: null, name_ar: "", name_en: "", default_points: 5, icon: "Star", color: "#27C9EE", active: true });
  const openEdit = (c, isGlobal = false) => setForm({ id: isGlobal ? null : c.id, globalKey: isGlobal ? c.key : null, name_ar: c.name_ar, name_en: c.name_en || "", default_points: c.default_points, icon: c.icon, color: c.color, active: c.active !== false });

  const save = async () => {
    const val = Number(form.default_points);
    if (!form.name_ar.trim() || !val) { toast.error(t("validation.required")); return; }
    setBusy(true);
    try {
      const payload = { name_ar: form.name_ar, name_en: form.name_en, default_points: val, icon: form.icon, color: form.color, active: form.active };
      if (form.id) await api.put(`/staff/workspace-categories/${form.id}`, payload);
      else if (form.globalKey) await api.put(`/staff/workspace-categories/override/${form.globalKey}`, payload);
      else await api.post("/staff/workspace-categories", payload);
      toast.success(t("teacher.cat_saved"));
      setForm(null);
      load();
      reload();
    } catch (e) { toast.error(apiError(e, t)); } finally { setBusy(false); }
  };

  const toggleActive = async (c) => {
    try {
      await api.put(`/staff/workspace-categories/${c.id}`, {
        name_ar: c.name_ar, name_en: c.name_en || "", default_points: c.default_points,
        icon: c.icon, color: c.color, active: !(c.active !== false),
      });
      load();
      reload();
    } catch (e) { toast.error(apiError(e, t)); }
  };

  const del = async (c) => {
    setBusy(true);
    try {
      await api.delete(`/staff/workspace-categories/${c.id}`);
      toast.success(t("teacher.cat_deleted"));
      setConfirmDel(null);
      load();
      reload();
    } catch (e) {
      if (e?.response?.data?.detail === "delete_blocked") {
        try {
          await api.put(`/staff/workspace-categories/${c.id}`, {
            name_ar: c.name_ar, name_en: c.name_en || "", default_points: c.default_points,
            icon: c.icon, color: c.color, active: false,
          });
          toast.success(t("teacher.cat_disabled_instead"));
          load();
          reload();
        } catch (e2) { toast.error(apiError(e2, t)); }
      } else {
        toast.error(apiError(e, t));
      }
      setConfirmDel(null);
    } finally { setBusy(false); }
  };

  const hideGlobal = async (c) => {
    setBusy(true);
    try {
      await api.put(`/staff/workspace-categories/override/${c.key}`, {
        name_ar: c.name_ar, name_en: c.name_en || "", default_points: c.default_points,
        icon: c.icon, color: c.color, active: false,
      });
      toast.success(t("teacher.cat_hidden"));
      load();
      reload();
    } catch (e) { toast.error(apiError(e, t)); } finally { setBusy(false); }
  };

  const resetOverride = async (c) => {
    setBusy(true);
    try {
      await api.delete(`/staff/workspace-categories/${c.id}`);
      toast.success(t("teacher.cat_reset_done"));
      load();
      reload();
    } catch (e) { toast.error(apiError(e, t)); } finally { setBusy(false); }
  };

  const fmtVal = (v) => (v > 0 ? `+${v}` : `${v}`);

  return (
    <div className="mt-6 border-t border-white/10 pt-5">
      <div className="flex items-center justify-between mb-3">
        <p className="font-extrabold">{t("teacher.ws_categories")}</p>
        <button data-testid="ws-cat-add" onClick={openNew} className="btn-magic btn-cyan text-xs px-3 py-1.5">
          <Plus className="w-3.5 h-3.5" /> {t("teacher.cat_add")}
        </button>
      </div>
      {!own ? null : own.length === 0 ? (
        <p data-testid="ws-cat-empty" className="text-xs text-[#A5A0D6] font-bold text-center py-3">{t("teacher.cat_none")}</p>
      ) : (
        <div className="space-y-2" data-testid="ws-cat-list">
          <p className="text-[11px] text-[#A5A0D6] font-bold">{t("teacher.cat_custom")}</p>
          {own.map((c) => (
            <div key={c.id} data-testid={`ws-cat-${c.key}`} className={`flex items-center gap-3 rounded-xl bg-[#0D0B38]/50 border border-white/10 px-3 py-2 ${c.active === false ? "opacity-50" : ""}`}>
              <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${c.color}26` }}>
                <Icon name={c.icon} className="w-4 h-4" style={{ color: c.color }} />
              </span>
              <span className="flex-1 text-sm font-bold truncate">{dispName(pick, c)}
                {c.active === false && <span className="ms-2 text-[10px] font-black text-[#FFC83D]">({t("teacher.disabled_tag")})</span>}
              </span>
              <span className={`text-sm font-black ${c.default_points > 0 ? "text-[#48D276]" : "text-[#FF8FB1]"}`} dir="ltr">{fmtVal(c.default_points)}</span>
              <button data-testid={`ws-cat-edit-${c.key}`} onClick={() => openEdit(c)} className="w-8 h-8 rounded-lg bg-white/8 hover:bg-white/15 flex items-center justify-center transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
              <button data-testid={`ws-cat-toggle-${c.key}`} onClick={() => toggleActive(c)} title={c.active !== false ? t("admin.disable") : t("admin.enable")}
                className={`w-8 h-8 rounded-lg border flex items-center justify-center transition-colors ${c.active !== false ? "bg-[#FFAE32]/12 border-[#FFAE32]/40 text-[#FFC83D]" : "bg-[#48D276]/10 border-[#48D276]/40 text-[#48D276]"}`}>
                {c.active !== false ? <Ban className="w-3.5 h-3.5" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
              </button>
              {c.is_override ? (
                <button data-testid={`ws-cat-reset-${c.key}`} disabled={busy} onClick={() => resetOverride(c)} title={t("teacher.cat_reset_default")}
                  className="w-8 h-8 rounded-lg bg-[#27C9EE]/10 border border-[#27C9EE]/40 text-[#27C9EE] flex items-center justify-center transition-colors hover:bg-[#27C9EE]/20">
                  <RotateCcw className="w-3.5 h-3.5" />
                </button>
              ) : confirmDel === c.id ? (
                <button data-testid={`ws-cat-del-confirm-${c.key}`} disabled={busy} onClick={() => del(c)}
                  className="h-8 px-2 rounded-lg bg-[#EF4D9A]/20 border border-[#EF4D9A]/50 text-[#FF8FB1] text-[10px] font-black transition-colors">
                  {t("teacher.cat_delete_confirm")}
                </button>
              ) : (
                <button data-testid={`ws-cat-del-${c.key}`} onClick={() => setConfirmDel(c.id)}
                  className="w-8 h-8 rounded-lg bg-[#EF4D9A]/10 border border-[#EF4D9A]/30 text-[#FF8FB1] flex items-center justify-center transition-colors hover:bg-[#EF4D9A]/20">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {form && (
        <div data-testid="ws-cat-form" className="mt-3 rounded-2xl bg-[#0D0B38]/60 border border-[#9C42F5]/40 p-4 space-y-3 animate-pop">
          <div className="grid grid-cols-2 gap-2">
            <input data-testid="ws-cat-name-ar" className="input-magic" dir="rtl" placeholder={`${t("admin.name_ar")} *`}
              value={form.name_ar} onChange={(e) => setForm({ ...form, name_ar: e.target.value })} />
            <input data-testid="ws-cat-name-en" className="input-magic" dir="ltr" placeholder={t("admin.name_en")}
              value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-2 items-end">
            <div>
              <label className="text-xs font-extrabold block mb-1 text-[#A5A0D6]">{t("teacher.cat_value")}</label>
              <input data-testid="ws-cat-points" type="number" min="-100" max="100" className="input-magic" dir="ltr"
                value={form.default_points} onChange={(e) => setForm({ ...form, default_points: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-extrabold block mb-1 text-[#A5A0D6]">{t("admin.color")}</label>
              <input data-testid="ws-cat-color" type="color" className="input-magic h-11" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} />
            </div>
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {CAT_ICONS.map((ic) => (
              <button key={ic} type="button" data-testid={`ws-cat-icon-${ic}`} onClick={() => setForm({ ...form, icon: ic })}
                className={`w-9 h-9 rounded-lg border flex items-center justify-center transition-all ${form.icon === ic ? "bg-[#7625D8]/40 border-[#9C42F5] glow-purple" : "bg-[#0D0B38]/50 border-white/12 hover:border-white/30"}`}>
                <Icon name={ic} className="w-4 h-4" />
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <button data-testid="ws-cat-cancel" onClick={() => setForm(null)} className="btn-magic btn-ghost flex-1 text-sm">{t("common.cancel")}</button>
            <button data-testid="ws-cat-save" disabled={busy} onClick={save} className="btn-magic flex-1 text-sm">{busy ? t("common.loading") : t("common.save")}</button>
          </div>
        </div>
      )}
      {globalCats.length > 0 && (
        <div className="mt-4">
          <p className="text-[11px] text-[#A5A0D6] font-bold">{t("teacher.cat_global")}</p>
          <p className="text-[10px] text-[#A5A0D6] font-semibold mb-2">{t("teacher.cat_global_hint")}</p>
          <div className="space-y-2" data-testid="ws-cat-global-list">
            {globalCats.map((c) => (
              <div key={c.id} data-testid={`ws-cat-global-${c.key}`} className="flex items-center gap-3 rounded-xl bg-[#0D0B38]/50 border border-white/10 px-3 py-2">
                <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${c.color}26` }}>
                  <Icon name={c.icon} className="w-4 h-4" style={{ color: c.color }} />
                </span>
                <span className="flex-1 text-sm font-bold truncate">{dispName(pick, c)}</span>
                <span className={`text-sm font-black ${c.default_points > 0 ? "text-[#48D276]" : "text-[#FF8FB1]"}`} dir="ltr">{fmtVal(c.default_points)}</span>
                <button data-testid={`ws-cat-edit-global-${c.key}`} onClick={() => openEdit(c, true)} title={t("teacher.cat_edit_default")}
                  className="w-8 h-8 rounded-lg bg-white/8 hover:bg-white/15 flex items-center justify-center transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
                <button data-testid={`ws-cat-hide-${c.key}`} disabled={busy} onClick={() => hideGlobal(c)} title={t("teacher.cat_hide")}
                  className="w-8 h-8 rounded-lg bg-[#FFAE32]/12 border border-[#FFAE32]/40 text-[#FFC83D] flex items-center justify-center transition-colors hover:bg-[#FFAE32]/20">
                  <Ban className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const BADGE_ICONS = ["Award", "Trophy", "Medal", "Crown", "Gem", "Star", "Sparkles", "Heart", "Target", "BookOpen"];

function BadgesSection() {
  const { t, pick } = useLang();
  const { reload } = useConfig();
  const [own, setOwn] = useState(null);
  const [globalBadges, setGlobalBadges] = useState([]);
  const [cats, setCats] = useState([]);
  const [catNames, setCatNames] = useState({});
  const [form, setForm] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [{ data: b }, { data: c }] = await Promise.all([
      api.get("/staff/workspace-badges"),
      api.get("/staff/workspace-categories"),
    ]);
    setOwn(b.own);
    const ownKeys = new Set(b.own.map((x) => x.key));
    setGlobalBadges(b.global.filter((g) => !ownKeys.has(g.key)));
    const merged = {};
    for (const g of c.global) merged[g.key] = g;
    for (const o of c.own) merged[o.key] = o;
    setCats(Object.values(merged).filter((x) => x.active !== false));
    setCatNames(Object.fromEntries(Object.values(merged).map((x) => [x.key, { name_ar: x.name_ar, name_en: x.name_en }])));
  }, []);
  useEffect(() => { load(); }, [load]);

  const blank = { id: null, globalKey: null, name_ar: "", name_en: "", desc_ar: "", desc_en: "", icon: "Award", color: "#FFC83D", active: true, auto_category: "", auto_count: 5 };
  const openNew = () => setForm({ ...blank });
  const openEdit = (b, isGlobal = false) => setForm({
    id: isGlobal ? null : b.id, globalKey: isGlobal ? b.key : null,
    name_ar: b.name_ar, name_en: b.name_en || "", desc_ar: b.desc_ar || "", desc_en: b.desc_en || "",
    icon: b.icon || "Award", color: b.color || "#FFC83D", active: b.active !== false,
    auto_category: b.auto?.category_key || "", auto_count: b.auto?.count || 5,
  });
  const payloadFrom = (f) => ({
    name_ar: f.name_ar, name_en: f.name_en, desc_ar: f.desc_ar, desc_en: f.desc_en,
    icon: f.icon, color: f.color, active: f.active,
    auto_category: f.auto_category || null,
    auto_count: f.auto_category ? Math.max(1, parseInt(f.auto_count, 10) || 1) : null,
  });

  const save = async () => {
    if (!form.name_ar.trim()) { toast.error(t("validation.required")); return; }
    if (form.auto_category && (!Number.isInteger(Number(form.auto_count)) || Number(form.auto_count) < 1)) { toast.error(t("validation.invalid_value")); return; }
    setBusy(true);
    try {
      const payload = payloadFrom(form);
      if (form.id) await api.put(`/staff/workspace-badges/${form.id}`, payload);
      else if (form.globalKey) await api.put(`/staff/workspace-badges/override/${form.globalKey}`, payload);
      else await api.post("/staff/workspace-badges", payload);
      toast.success(t("teacher.badge_saved"));
      setForm(null);
      load();
      reload();
    } catch (e) { toast.error(apiError(e, t)); } finally { setBusy(false); }
  };

  const toggleActive = async (b) => {
    try {
      await api.put(`/staff/workspace-badges/${b.id}`, payloadFrom({ ...openEditPayload(b), active: b.active === false }));
      load();
      reload();
    } catch (e) { toast.error(apiError(e, t)); }
  };
  const openEditPayload = (b) => ({
    name_ar: b.name_ar, name_en: b.name_en || "", desc_ar: b.desc_ar || "", desc_en: b.desc_en || "",
    icon: b.icon || "Award", color: b.color || "#FFC83D",
    auto_category: b.auto?.category_key || "", auto_count: b.auto?.count || 5,
  });

  const del = async (b) => {
    setBusy(true);
    try {
      await api.delete(`/staff/workspace-badges/${b.id}`);
      toast.success(t("teacher.badge_deleted"));
      setConfirmDel(null);
      load();
      reload();
    } catch (e) { toast.error(apiError(e, t)); setConfirmDel(null); } finally { setBusy(false); }
  };

  const autoTag = (b) => {
    if (!b.auto?.category_key || !b.auto?.count) return null;
    const cn = catNames[b.auto.category_key];
    return `${cn ? pick(cn, "name") || cn.name_ar : b.auto.category_key} ×${b.auto.count}`;
  };

  const renderRow = (b, isGlobal) => (
    <div key={b.id} data-testid={`ws-badge-${b.key}`} className={`flex items-center gap-3 rounded-xl bg-[#0D0B38]/50 border border-white/10 px-3 py-2 ${b.active === false ? "opacity-50" : ""}`}>
      <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${b.color}26` }}>
        <Icon name={b.icon} className="w-4 h-4" style={{ color: b.color }} />
      </span>
      <span className="flex-1 text-sm font-bold truncate">{dispName(pick, b)}
        {autoTag(b) && <span className="ms-2 text-[10px] font-black text-[#27C9EE]" dir="ltr">{autoTag(b)}</span>}
        {b.active === false && <span className="ms-2 text-[10px] font-black text-[#FFC83D]">({t("teacher.disabled_tag")})</span>}
      </span>
      <button data-testid={`ws-badge-edit-${b.key}`} onClick={() => openEdit(b, isGlobal)} className="w-8 h-8 rounded-lg bg-white/8 hover:bg-white/15 flex items-center justify-center transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
      {!isGlobal && (
        <>
          <button data-testid={`ws-badge-toggle-${b.key}`} onClick={() => toggleActive(b)} title={b.active !== false ? t("admin.disable") : t("admin.enable")}
            className={`w-8 h-8 rounded-lg border flex items-center justify-center transition-colors ${b.active !== false ? "bg-[#FFAE32]/12 border-[#FFAE32]/40 text-[#FFC83D]" : "bg-[#48D276]/10 border-[#48D276]/40 text-[#48D276]"}`}>
            {b.active !== false ? <Ban className="w-3.5 h-3.5" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
          </button>
          {confirmDel === b.id ? (
            <button data-testid={`ws-badge-del-confirm-${b.key}`} disabled={busy} onClick={() => del(b)}
              className="h-8 px-2 rounded-lg bg-[#EF4D9A]/20 border border-[#EF4D9A]/50 text-[#FF8FB1] text-[10px] font-black transition-colors">
              {t("teacher.badge_delete_confirm")}
            </button>
          ) : (
            <button data-testid={`ws-badge-del-${b.key}`} onClick={() => setConfirmDel(b.id)}
              className="w-8 h-8 rounded-lg bg-[#EF4D9A]/10 border border-[#EF4D9A]/30 text-[#FF8FB1] flex items-center justify-center transition-colors hover:bg-[#EF4D9A]/20">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </>
      )}
    </div>
  );

  return (
    <div className="mt-6 border-t border-white/10 pt-5">
      <div className="flex items-center justify-between mb-3">
        <p className="font-extrabold">{t("teacher.ws_badges")}</p>
        <button data-testid="ws-badge-add" onClick={openNew} className="btn-magic btn-cyan text-xs px-3 py-1.5">
          <Plus className="w-3.5 h-3.5" /> {t("teacher.badge_add")}
        </button>
      </div>
      {!own ? null : (
        <div className="space-y-2" data-testid="ws-badge-list">
          {own.length === 0 && globalBadges.length === 0 ? (
            <p data-testid="ws-badge-empty" className="text-xs text-[#A5A0D6] font-bold text-center py-3">{t("teacher.badge_none")}</p>
          ) : (
            <>
              {own.length > 0 && <p className="text-[11px] text-[#A5A0D6] font-bold">{t("teacher.badge_custom")}</p>}
              {own.map((b) => renderRow(b, false))}
              {globalBadges.length > 0 && (
                <>
                  <p className="text-[11px] text-[#A5A0D6] font-bold mt-3">{t("teacher.badge_global")}</p>
                  <p className="text-[10px] text-[#A5A0D6] font-semibold -mt-1">{t("teacher.badge_global_hint")}</p>
                  <div data-testid="ws-badge-global-list" className="space-y-2">{globalBadges.map((b) => renderRow(b, true))}</div>
                </>
              )}
            </>
          )}
        </div>
      )}
      {form && (
        <div data-testid="ws-badge-form" className="mt-3 rounded-2xl bg-[#0D0B38]/60 border border-[#9C42F5]/40 p-4 space-y-3 animate-pop">
          <div className="grid grid-cols-2 gap-2">
            <input data-testid="ws-badge-name-ar" className="input-magic" dir="rtl" placeholder={`${t("admin.name_ar")} *`}
              value={form.name_ar} onChange={(e) => setForm({ ...form, name_ar: e.target.value })} />
            <input data-testid="ws-badge-name-en" className="input-magic" dir="ltr" placeholder={t("admin.name_en")}
              value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input data-testid="ws-badge-desc-ar" className="input-magic" dir="rtl" placeholder={t("teacher.badge_desc_ar")}
              value={form.desc_ar} onChange={(e) => setForm({ ...form, desc_ar: e.target.value })} />
            <input data-testid="ws-badge-desc-en" className="input-magic" dir="ltr" placeholder={t("teacher.badge_desc_en")}
              value={form.desc_en} onChange={(e) => setForm({ ...form, desc_en: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-2 items-end">
            <div>
              <label className="text-xs font-extrabold block mb-1 text-[#A5A0D6]">{t("admin.color")}</label>
              <input data-testid="ws-badge-color" type="color" className="input-magic h-11" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-extrabold block mb-1 text-[#A5A0D6]">{t("teacher.badge_auto_rule")}</label>
              <div className="grid grid-cols-2 gap-2">
                <select data-testid="ws-badge-auto-cat" className="input-magic" value={form.auto_category}
                  onChange={(e) => setForm({ ...form, auto_category: e.target.value })}>
                  <option value="">{t("teacher.badge_no_auto")}</option>
                  {cats.map((c) => <option key={c.key} value={c.key}>{c.name_ar}{c.name_en ? ` — ${c.name_en}` : ""}</option>)}
                </select>
                <input data-testid="ws-badge-auto-count" type="number" min="1" max="1000" className="input-magic" dir="ltr"
                  disabled={!form.auto_category} placeholder={t("teacher.badge_auto_count")}
                  value={form.auto_count} onChange={(e) => setForm({ ...form, auto_count: e.target.value })} />
              </div>
            </div>
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {BADGE_ICONS.map((ic) => (
              <button key={ic} type="button" data-testid={`ws-badge-icon-${ic}`} onClick={() => setForm({ ...form, icon: ic })}
                className={`w-9 h-9 rounded-lg border flex items-center justify-center transition-all ${form.icon === ic ? "bg-[#7625D8]/40 border-[#9C42F5] glow-purple" : "bg-[#0D0B38]/50 border-white/12 hover:border-white/30"}`}>
                <Icon name={ic} className="w-4 h-4" />
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <button data-testid="ws-badge-cancel" onClick={() => setForm(null)} className="btn-magic btn-ghost flex-1 text-sm">{t("common.cancel")}</button>
            <button data-testid="ws-badge-save" disabled={busy} onClick={save} className="btn-magic flex-1 text-sm">{busy ? t("common.loading") : t("common.save")}</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function WorkspaceLabelsModal({ open, onClose }) {
  const { t } = useLang();
  const { config, reload } = useConfig();
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      const labels = config?.ui_labels || {};
      const init = {};
      for (const f of FIELDS) {
        init[`${f.key}_ar`] = labels[f.key]?.ar || "";
        init[`${f.key}_en`] = labels[f.key]?.en || "";
      }
      setForm(init);
    }
  }, [open, config]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/staff/workspace-labels", form);
      toast.success(t("teacher.ws_saved"));
      await reload();
      onClose();
    } catch (e) {
      toast.error(apiError(e, t));
    } finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title={t("teacher.workspace_settings")} testId="workspace-labels-modal" wide>
      <p className="text-xs text-[#A5A0D6] font-bold mb-4">{t("teacher.ws_hint")}</p>
      <div className="space-y-4">
        {FIELDS.map((f) => (
          <div key={f.key} className="rounded-2xl bg-[#0D0B38]/50 border border-white/10 p-3.5">
            <p className="text-sm font-extrabold mb-2">{t(f.labelKey)}</p>
            <div className="grid grid-cols-2 gap-2">
              <input data-testid={`ws-label-${f.key}-ar`} className="input-magic" dir="rtl"
                placeholder={`${t("teacher.ws_ar")}: ${f.defAr}`}
                value={form[`${f.key}_ar`] || ""} onChange={(e) => setForm({ ...form, [`${f.key}_ar`]: e.target.value })} />
              <input data-testid={`ws-label-${f.key}-en`} className="input-magic" dir="ltr"
                placeholder={`${t("teacher.ws_en")}: ${f.defEn}`}
                value={form[`${f.key}_en`] || ""} onChange={(e) => setForm({ ...form, [`${f.key}_en`]: e.target.value })} />
            </div>
          </div>
        ))}
      </div>
      <button data-testid="ws-labels-save" disabled={busy} onClick={save} className="btn-magic w-full mt-5">
        {busy ? t("common.loading") : t("common.save")}
      </button>
      <CategoriesSection />
      <BadgesSection />
    </Modal>
  );
}
